import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import ShopAuthLayout, { ArrowRightIcon } from './ShopAuthLayout';
import {
    getShopAuthState,
    requestShopPasswordReset,
    resetShopPassword,
    verifyShopPasswordResetCode,
} from '../../utils/shopAuthApi';

const FIELD_LABEL_CLASS = 'text-[11px] font-black uppercase tracking-[0.18em] text-[#606060]';
const INPUT_CLASS =
    'h-12 w-full rounded-xl border border-[#d8cbbd] bg-[#fbfaf8] px-4 text-[15px] font-semibold text-[#414141] shadow-[0_2px_7px_rgba(35,31,25,0.12)] outline-none transition placeholder:font-medium placeholder:text-[#a9a9a9] focus:border-[#D81F26] focus:ring-4 focus:ring-[#D81F26]/10 sm:text-base';

// Matches OTP_LENGTH in auth/otp_store.py.
const CODE_LENGTH = 6;
const MIN_PASSWORD_LENGTH = 8;

const STEP_COPY = {
    1: { title: 'Forgot your password?', blurb: 'Tell us how you sign in and we will send a code.' },
    2: { title: 'Check for your code.', blurb: 'It expires in a few minutes, so use it while it is fresh.' },
    // "everywhere else" was wrong: reset-password deletes every session for the account,
    // this one included, so they land back on the login page.
    3: { title: 'Set a new password.', blurb: 'This signs your shop out everywhere, so sign in again after.' },
};

function Field({ id, label, ...props }) {
    return (
        <label className="block" htmlFor={id}>
            <span className={FIELD_LABEL_CLASS}>{label}</span>
            <input id={id} className={`mt-2 ${INPUT_CLASS}`} {...props} />
        </label>
    );
}

export default function ShopForgotPasswordPage() {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    // Settings sends an owner here who is signed in but cannot remember their password —
    // they need it to change the email they sign in with. Without this flag the guard
    // below would bounce them straight back to the dashboard, which is what made that
    // link look broken.
    const cameFromSettings = searchParams.get('from') === 'settings';
    const [step, setStep] = useState(1);
    const [identifier, setIdentifier] = useState('');
    // What the server normalized and keyed the code on. Every later call sends this,
    // never what is still sitting in the input box.
    const [target, setTarget] = useState(null);
    const [otp, setOtp] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [cooldown, setCooldown] = useState(0);
    // Set when the server says nothing is registered for what they typed, so the error
    // can point them at signup instead of just refusing.
    const [isUnregistered, setIsUnregistered] = useState(false);
    const codeInputRef = useRef(null);

    // Someone who is already signed in has no business on a password-reset screen —
    // unless they asked for it on purpose, which is what cameFromSettings means.
    useEffect(() => {
        if (cameFromSettings) return undefined;
        let cancelled = false;
        getShopAuthState()
            .then((data) => {
                if (!cancelled && data?.authenticated) navigate('/', { replace: true });
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [navigate, cameFromSettings]);

    // Resend countdown. otp_store enforces the same cooldown server-side; this only
    // keeps the button from firing requests that are already guaranteed to 429.
    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const timer = setInterval(() => setCooldown((current) => Math.max(0, current - 1)), 1000);
        return () => clearInterval(timer);
    }, [cooldown]);

    useEffect(() => {
        if (step === 2) codeInputRef.current?.focus();
    }, [step]);

    const progress = useMemo(() => `${Math.round((step / 3) * 100)}%`, [step]);

    const applyError = useCallback((err, fallback) => {
        if (err?.retryAfterSeconds) setCooldown(err.retryAfterSeconds);
        setIsUnregistered(err?.status === 404);
        setError(err?.message || fallback);
    }, []);

    const sendCode = useCallback(
        async (rawIdentifier, { isResend = false } = {}) => {
            const data = await requestShopPasswordReset({ identifier: rawIdentifier });
            setTarget(data);
            // The server tells us its own cooldown, so the countdown cannot drift out of
            // sync if OTP_RESEND_COOLDOWN_SECONDS is retuned in .env.
            setCooldown(Number(data?.resend_after) > 0 ? Math.ceil(Number(data.resend_after)) : 60);
            setNotice(`${isResend ? 'New code sent' : 'Code sent'} to ${data?.sent_to || 'your account'}.`);
            return data;
        },
        [],
    );

    const handleRequest = async (event) => {
        event.preventDefault();
        if (!identifier.trim()) {
            setError('Enter the email or phone you sign in with.');
            return;
        }
        setError('');
        setIsSubmitting(true);
        try {
            await sendCode(identifier);
            setOtp('');
            setStep(2);
        } catch (err) {
            applyError(err, 'Could not send the reset code.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleResend = async () => {
        if (cooldown > 0 || isSubmitting) return;
        setError('');
        setIsSubmitting(true);
        try {
            await sendCode(target?.identifier || identifier, { isResend: true });
            setOtp('');
            codeInputRef.current?.focus();
        } catch (err) {
            applyError(err, 'Could not resend the code.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleVerify = async (event) => {
        event.preventDefault();
        if (otp.trim().length !== CODE_LENGTH) {
            setError(`Enter the ${CODE_LENGTH}-digit code.`);
            return;
        }
        setError('');
        setNotice('');
        setIsSubmitting(true);
        try {
            await verifyShopPasswordResetCode({ identifier: target.identifier, otp: otp.trim() });
            setStep(3);
        } catch (err) {
            setOtp('');
            applyError(err, 'Could not verify that code.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleReset = async (event) => {
        event.preventDefault();
        if (password.length < MIN_PASSWORD_LENGTH) {
            setError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters.`);
            return;
        }
        if (password !== confirmPassword) {
            setError('Both passwords must match.');
            return;
        }
        setError('');
        setIsSubmitting(true);
        try {
            await resetShopPassword({
                identifier: target.identifier,
                otp: otp.trim(),
                newPassword: password,
            });
            navigate('/login', { replace: true, state: { passwordReset: true } });
        } catch (err) {
            // The code can lapse between screens. Send them back to enter a fresh one
            // rather than stranding them on a form that can no longer succeed.
            if (err?.status === 401) {
                setOtp('');
                setPassword('');
                setConfirmPassword('');
                setNotice('');
                setStep(2);
            }
            applyError(err, 'Could not reset your password.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const submitHandlers = { 1: handleRequest, 2: handleVerify, 3: handleReset };
    const submitLabels = {
        1: isSubmitting ? 'Sending code...' : 'Send code',
        2: isSubmitting ? 'Checking...' : 'Verify code',
        3: isSubmitting ? 'Saving...' : 'Reset password',
    };

    return (
        <ShopAuthLayout>
            <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                    <p className="text-[10px] font-black uppercase tracking-[0.22em] text-[#D81F26] sm:text-[11px]">Shop portal recovery</p>
                    <h1 className="mt-1.5 text-2xl font-black leading-tight tracking-tight text-[#414141] sm:mt-2 sm:text-[28px]">{STEP_COPY[step].title}</h1>
                    <p className="mt-2 text-[13px] font-semibold leading-relaxed text-[#606060] sm:mt-2.5 sm:text-sm">{STEP_COPY[step].blurb}</p>
                </div>
                <p className="mt-1 shrink-0 font-mono text-[11px] font-bold text-[#6B6B6B]">0{step} / 03</p>
            </div>

            <div className="mt-6 h-1 rounded-full bg-[#ece8e2]">
                <div className="h-full rounded-full bg-[#D81F26] transition-all duration-300" style={{ width: progress }} />
            </div>

            <form className="mt-7 space-y-5 sm:mt-8" onSubmit={submitHandlers[step]}>
                {step === 1 ? (
                    <Field
                        id="shop-reset-identifier"
                        label="Work email or phone"
                        autoComplete="username"
                        placeholder="you@shop.com"
                        value={identifier}
                        onChange={(event) => {
                            setIdentifier(event.target.value);
                            if (error) {
                                setError('');
                                setIsUnregistered(false);
                            }
                        }}
                        disabled={isSubmitting}
                        required
                    />
                ) : null}

                {step === 2 ? (
                    <div>
                        <div className="mb-2 flex items-center justify-between gap-4">
                            <label className={FIELD_LABEL_CLASS} htmlFor="shop-reset-code">{CODE_LENGTH}-digit code</label>
                            <button
                                type="button"
                                onClick={handleResend}
                                disabled={cooldown > 0 || isSubmitting}
                                className="text-[11px] font-black text-[#D81F26] transition hover:text-[#414141] disabled:cursor-not-allowed disabled:text-[#a9a9a9]"
                            >
                                {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                            </button>
                        </div>
                        <input
                            id="shop-reset-code"
                            ref={codeInputRef}
                            className={`${INPUT_CLASS} text-center font-mono tracking-[0.5em]`}
                            inputMode="numeric"
                            autoComplete="one-time-code"
                            maxLength={CODE_LENGTH}
                            placeholder="000000"
                            value={otp}
                            onChange={(event) => {
                                setOtp(event.target.value.replace(/\D/g, '').slice(0, CODE_LENGTH));
                                if (error) setError('');
                            }}
                            disabled={isSubmitting}
                            required
                        />
                    </div>
                ) : null}

                {step === 3 ? (
                    <>
                        <Field
                            id="shop-reset-password"
                            label="New password"
                            type="password"
                            autoComplete="new-password"
                            placeholder={`${MIN_PASSWORD_LENGTH}+ characters`}
                            value={password}
                            onChange={(event) => {
                                setPassword(event.target.value);
                                if (error) setError('');
                            }}
                            disabled={isSubmitting}
                            required
                        />
                        <Field
                            id="shop-reset-password-confirm"
                            label="Confirm password"
                            type="password"
                            autoComplete="new-password"
                            placeholder="Type it once more"
                            value={confirmPassword}
                            onChange={(event) => {
                                setConfirmPassword(event.target.value);
                                if (error) setError('');
                            }}
                            disabled={isSubmitting}
                            required
                        />
                    </>
                ) : null}

                {notice && !error ? (
                    <p className="rounded-xl border border-[#d8cbbd] bg-[#fbfaf8] px-4 py-2.5 text-[13px] font-bold text-[#606060]">{notice}</p>
                ) : null}

                {error ? (
                    <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-2.5 text-[13px] font-bold text-red-700">
                        <p>{error}</p>
                        {isUnregistered ? (
                            <Link className="mt-1 inline-flex items-center gap-1.5 font-black text-[#D81F26] underline transition hover:text-[#414141]" to="/signup">
                                Create a shop account
                                <ArrowRightIcon className="h-3.5 w-3.5" />
                            </Link>
                        ) : null}
                    </div>
                ) : null}

                <div className={step === 1 ? '' : 'grid grid-cols-[76px_1fr] gap-3'}>
                    {step > 1 ? (
                        <button
                            type="button"
                            className="h-12 rounded-xl border border-[#e2d7ca] bg-[#fbfaf8] text-[12px] font-black uppercase tracking-[0.12em] text-[#414141] transition hover:bg-white active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 sm:text-[11px]"
                            onClick={() => {
                                setError('');
                                setNotice('');
                                setStep(step - 1);
                            }}
                            disabled={isSubmitting}
                        >
                            Back
                        </button>
                    ) : null}
                    <button
                        type="submit"
                        disabled={isSubmitting}
                        className="flex h-12 w-full items-center justify-center gap-2.5 rounded-xl bg-[#D81F26] px-4 text-[12px] font-black uppercase tracking-[0.16em] text-white transition hover:bg-[#B8181E] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60 sm:text-[11px]"
                    >
                        {submitLabels[step]}
                        <ArrowRightIcon className="h-4 w-4 shrink-0" />
                    </button>
                </div>
            </form>

            <p className="mt-7 text-center text-[12px] font-semibold text-[#606060]">
                Remembered it? <Link className="font-black text-[#D81F26] transition hover:text-[#414141]" to="/login">Sign in</Link>
            </p>
        </ShopAuthLayout>
    );
}
